import { Button } from "@heroui/react";
import { Plus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { KeywordListTable } from "@/components/keyword/table";
import { useGetAllKeywords } from "@/hooks/api/keyword/useGetAllKeywords";
import { AppPage } from "@/layouts/page";

export function KeywordsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [limit, setLimit] = useState(20);
  const [isAdding, setIsAdding] = useState(false);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1") || 1);

  const { data: keywords, isFetching } = useGetAllKeywords({
    searchTerm,
    page,
    limit,
  });

  const changePage = (next: number) => {
    const params = new URLSearchParams(searchParams);

    params.set("page", next.toString());
    setSearchParams(params);
  };

  // A new search or page size starts again from page one.
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }
    if (page !== 1) changePage(1);
  }, [searchTerm, limit]);

  const total = keywords?.total;

  return (
    <div className="flex min-h-0 w-full max-w-3xl flex-1 flex-col">
      <AppPage
        action={
          <Button onPress={() => setIsAdding(true)}>
            <Plus className="size-4" />
            Add Keyword
          </Button>
        }
        subtitle={
          total === undefined
            ? undefined
            : `${total.toLocaleString()} search keywords and SEO terms`
        }
        title="Keywords"
      >
        <KeywordListTable
          changePage={changePage}
          isAdding={isAdding}
          isLoading={isFetching && !keywords}
          items={keywords?.data ?? []}
          limit={limit}
          page={page}
          searchTerm={searchTerm}
          totalKeywords={total ?? 0}
          onCloseAdd={() => setIsAdding(false)}
          onLimitChange={setLimit}
          onSearchChange={setSearchTerm}
        />
      </AppPage>
    </div>
  );
}
